// Owner 2026-10-09: "View" on an FTO or COA should show the request's DETAILS, not the request
// form. This read-only card is what the eye icon opens (…/fto/:id?view=1, …/coa/:id?view=1);
// the pencil still opens the form, and the "Open form" button here does the same for people
// who may edit or approve (the form itself keeps its read-only rules).
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, CardHeader, Row, Col, Badge, Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { getFtoRequest, getCoaRequest } from "./ftoService";

const fmtDate = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const fmtTime = (v) => (v ? String(v).slice(0, 5) : "—");
const dayOption = (v) => ({ FULL: "full day", AM: "first half", PM: "second half", FIRST: "first half", SECOND: "second half" }[String(v || "FULL").toUpperCase()] || String(v));
const Decision = ({ value }) =>
    value === true ? <Badge color="success" pill>Approved</Badge>
    : value === false ? <Badge color="danger" pill>Denied</Badge>
    : <Badge color="warning" pill>Pending</Badge>;

const Line = ({ label, children }) => (
    <Row className="py-2 border-bottom gx-2">
        <Col sm={4} md={3} className="text-muted small">{label}</Col>
        <Col sm={8} md={9} style={{ fontSize: 14 }}>{children ?? "—"}</Col>
    </Row>
);

const RequestDetails = ({ kind, id }) => {
    const navigate = useNavigate();
    const isCoa = kind === "coa";
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        (isCoa ? getCoaRequest(id) : getFtoRequest(id))
            .then(({ data: d }) => { if (alive) setData(d); })
            .catch((e) => toast.error(e?.response?.status === 403
                ? "You cannot see this request."
                : `Failed to load ${isCoa ? "COA" : "FTO"} request #${id}.`))
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [kind, id, isCoa]);

    if (loading) return <div className="d-flex justify-content-center py-5"><Spinner color="primary" /></div>;
    if (!data) return (
        <Card><CardBody className="text-center text-muted py-4">
            Nothing to show. <Button color="link" size="sm" onClick={() => navigate("/attendance")}>Back to my requests</Button>
        </CardBody></Card>
    );

    const overall = data.hrApproval === true ? { label: "Approved", color: "success" }
        : (data.hrApproval === false || data.supervisorApproval === false) ? { label: "Denied", color: "danger" }
        : { label: "Pending", color: "warning" };
    const formPath = `/attendance/${isCoa ? "coa" : "fto"}/${id}`;

    return (
        <Row className="g-3">
            <Col xl={8} lg={9}>
                <Card>
                    <CardHeader className="d-flex align-items-center gap-2 py-3">
                        <h5 className="card-title mb-0 flex-grow-1" style={{ fontSize: 15 }}>
                            {isCoa ? "COA" : "FTO"} Request #{id}
                        </h5>
                        <Badge color={overall.color} pill>{overall.label}</Badge>
                    </CardHeader>
                    <CardBody className="pt-2">
                        <Line label="Employee">{data.empName ? `${data.empName} (${data.empId})` : data.empId}</Line>
                        {isCoa ? (
                            <>
                                <Line label="Date">{fmtDate(data.startDate)}{data.endDate ? ` to ${fmtDate(data.endDate)}` : ""}</Line>
                                <Line label="Changed hours">{fmtTime(data.modifiedStartTime)} to {fmtTime(data.modifiedEndTime)}</Line>
                            </>
                        ) : (
                            <>
                                <Line label="Dates">
                                    {fmtDate(data.startDate)} ({dayOption(data.startDayOption)})
                                    {data.endDate ? ` to ${fmtDate(data.endDate)} (${dayOption(data.endDayOption)})` : ""}
                                </Line>
                                <Line label="Type">
                                    {data.typeOfLeave === "OTHER" ? `Other: ${data.otherReason || "—"}`
                                        : data.typeOfLeave === "COMPANY" ? "Company business" : "Vacation time"}
                                    {" · "}{data.scheduledStatus || "Scheduled"}{" · "}{data.payStatus || "Paid"}
                                </Line>
                                <Line label="FTO days">{data.ftoCount ?? 0}{data.exemption ? " · exemption" : ""}</Line>
                            </>
                        )}
                        <Line label="Employee comment">{data.employeeComment || "—"}</Line>
                        <Line label="Supervisor"><Decision value={data.supervisorApproval} />{data.supervisorNote ? <span className="ms-2 text-muted">{data.supervisorNote}</span> : null}</Line>
                        <Line label="HR"><Decision value={data.hrApproval} />{data.hrNote ? <span className="ms-2 text-muted">{data.hrNote}</span> : null}</Line>
                        {!isCoa && data.mpcApproval !== undefined && data.mpcApproval !== null && (
                            <Line label="MPC"><Decision value={data.mpcApproval} /></Line>
                        )}
                        <div className="d-flex gap-2 mt-3">
                            <Button color="light" size="sm" onClick={() => navigate("/attendance")}>
                                <i className="ri-arrow-left-line align-bottom me-1" />Back to my requests
                            </Button>
                            <Button color="primary" size="sm" outline onClick={() => navigate(formPath)}>
                                <i className="ri-pencil-line align-bottom me-1" />Open form
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    );
};

export default RequestDetails;
